import { useState, useEffect } from 'react';
import {
  XMarkIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  LockClosedIcon,
  EyeIcon,
  EyeSlashIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { integrationsApi, type POSSystem } from '../../api/client';

type POSType = 'toast' | 'square' | 'clover' | 'lightspeed' | 'revel' | 'touchbistro';

interface ConnectPOSModalProps {
  storeId: string;
  storeName: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'select' | 'connect' | 'success';

export function ConnectPOSModal({
  storeId,
  storeName,
  isOpen,
  onClose,
  onSuccess,
}: ConnectPOSModalProps) {
  const [step, setStep] = useState<Step>('select');
  const [availablePOS, setAvailablePOS] = useState<POSSystem[]>([]);
  const [selectedPOS, setSelectedPOS] = useState<POSSystem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showManual, setShowManual] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // API Key credentials (for Revel, TouchBistro)
  const [credentials, setCredentials] = useState({
    api_key: '',
    api_secret: '',
    merchant_id: '',
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchAvailablePOS();
      // Reset state when modal opens
      setStep('select');
      setSelectedPOS(null);
      setError('');
      setShowManual(false);
      setCredentials({ api_key: '', api_secret: '', merchant_id: '' });
    }
  }, [isOpen]);

  async function fetchAvailablePOS() {
    try {
      const res = await integrationsApi.availablePOS();
      setAvailablePOS(res.data.data);
    } catch (err) {
      console.error('Failed to fetch POS systems:', err);
    }
  }

  function handleSelectPOS(pos: POSSystem) {
    setSelectedPOS(pos);
    setStep('connect');
    setError('');
  }

  async function handleOAuthConnect() {
    if (!selectedPOS) return;
    setError('');

    // For OAuth-based POS, we would redirect to their auth page
    // For now, show a message that OAuth isn't configured
    setError(`OAuth connection for ${selectedPOS.name} requires API credentials. Please use manual setup.`);
    setShowManual(true);
  }

  async function handleManualConnect() {
    if (!selectedPOS) return;

    if (!credentials.api_key) {
      setError('API Key is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await integrationsApi.connectPOS({
        store_id: storeId,
        pos_type: selectedPOS.type as POSType,
        credentials: {
          api_key: credentials.api_key,
          api_secret: credentials.api_secret || undefined,
          merchant_id: credentials.merchant_id || undefined,
        },
      });

      setStep('success');
      setSyncing(true);

      // Trigger initial sync
      try {
        const posRes = await integrationsApi.listPOS();
        const newIntegration = posRes.data.data.find(
          p => p.store_id === storeId && p.pos_type === selectedPOS.type
        );
        if (newIntegration) {
          await integrationsApi.syncPOS(newIntegration.id);
        }
      } catch {
        // Sync failure is non-critical
      }

      setSyncing(false);
    } catch (err: unknown) {
      const axiosError = err as { response?: { data?: { message?: string } } };
      setError(axiosError.response?.data?.message || 'Failed to connect POS. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  function handleDone() {
    onSuccess();
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-3">
            {step !== 'select' && step !== 'success' && (
              <button
                onClick={() => {
                  if (step === 'connect') {
                    setStep('select');
                    setSelectedPOS(null);
                    setShowManual(false);
                    setCredentials({ api_key: '', api_secret: '', merchant_id: '' });
                  }
                }}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
            )}
            <div>
              <h3 className="text-lg font-semibold text-slate-900">
                {step === 'select' && 'Connect Your POS'}
                {step === 'connect' && `Connect ${selectedPOS?.name}`}
                {step === 'success' && 'Connected!'}
              </h3>
              {step !== 'success' && (
                <p className="text-sm text-slate-500">{storeName}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Step 1: Select POS */}
          {step === 'select' && (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Sync orders automatically. No more CSV uploads.
              </p>

              <div className="grid grid-cols-3 gap-3">
                {availablePOS.map((pos) => (
                  <button
                    key={pos.type}
                    onClick={() => handleSelectPOS(pos)}
                    className="flex flex-col items-center gap-2 p-4 bg-white border-2 border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all group"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 font-bold group-hover:bg-blue-100 group-hover:text-blue-600">
                      {pos.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-blue-700">
                      {pos.name}
                    </span>
                    {pos.type === 'toast' && (
                      <span className="text-xs text-amber-600 font-medium">Popular</span>
                    )}
                  </button>
                ))}
              </div>

              <p className="text-xs text-slate-500 text-center pt-2">
                Don't see your POS?{' '}
                <a href="mailto:support@recoverrefunds.com" className="text-blue-600 hover:underline">
                  Request it
                </a>
              </p>
            </div>
          )}

          {/* Step 2: Connect */}
          {step === 'connect' && selectedPOS && (
            <div className="space-y-4">
              {error && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <ExclamationTriangleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {!showManual && selectedPOS.auth_type === 'oauth2' && (
                <>
                  <div className="text-center py-4">
                    <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <LockClosedIcon className="w-8 h-8 text-slate-600" />
                    </div>
                    <h4 className="font-semibold text-slate-900 mb-2">Secure Connection</h4>
                    <p className="text-sm text-slate-600 mb-4">
                      You'll be redirected to {selectedPOS.name} to authorize RecoverRefunds.
                    </p>
                  </div>

                  <div className="bg-slate-50 rounded-xl p-4 space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>Order history (read-only)</span>
                    </div>
                    <div className="flex items-center gap-2 text-emerald-700">
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>Refund records (read-only)</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500">
                      <XMarkIcon className="w-4 h-4" />
                      <span>We NEVER access payment info</span>
                    </div>
                  </div>

                  <button
                    onClick={handleOAuthConnect}
                    disabled={loading}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        Connecting...
                      </span>
                    ) : (
                      `Connect with ${selectedPOS.name}`
                    )}
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-slate-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <button
                        onClick={() => setShowManual(true)}
                        className="bg-white px-4 text-slate-500 hover:text-slate-700"
                      >
                        Or enter credentials manually
                      </button>
                    </div>
                  </div>
                </>
              )}

              {(showManual || selectedPOS.auth_type === 'api_key') && (
                <div className="space-y-4">
                  <p className="text-sm text-slate-600">
                    Enter your {selectedPOS.name} API credentials to connect.
                  </p>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      API Key <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={credentials.api_key}
                        onChange={(e) => setCredentials({ ...credentials, api_key: e.target.value })}
                        placeholder="Enter your API key"
                        className="w-full px-4 py-2.5 pr-10 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKey(!showApiKey)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showApiKey ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      API Secret <span className="text-slate-400">(if required)</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showApiSecret ? 'text' : 'password'}
                        value={credentials.api_secret}
                        onChange={(e) => setCredentials({ ...credentials, api_secret: e.target.value })}
                        placeholder="Enter your API secret"
                        className="w-full px-4 py-2.5 pr-10 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiSecret(!showApiSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showApiSecret ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      Merchant/Restaurant ID <span className="text-slate-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={credentials.merchant_id}
                      onChange={(e) => setCredentials({ ...credentials, merchant_id: e.target.value })}
                      placeholder="Enter your merchant ID"
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <a
                    href="#"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    How to find your {selectedPOS.name} API credentials
                  </a>

                  <button
                    onClick={handleManualConnect}
                    disabled={loading || !credentials.api_key}
                    className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center gap-2">
                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        Connecting...
                      </span>
                    ) : (
                      'Connect'
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Success */}
          {step === 'success' && selectedPOS && (
            <div className="text-center py-4 space-y-4">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircleIcon className="w-10 h-10 text-emerald-600" />
              </div>

              <div>
                <h4 className="text-lg font-semibold text-slate-900 mb-1">Connected Successfully</h4>
                <p className="text-sm text-slate-600">
                  {selectedPOS.name} is now syncing with your {storeName} store.
                </p>
              </div>

              <p className="text-sm text-slate-500">
                Orders will import automatically every 15 minutes.
              </p>

              {syncing && (
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-center gap-3">
                  <ArrowPathIcon className="w-5 h-5 text-blue-600 animate-spin" />
                  <div className="text-left">
                    <p className="text-sm font-medium text-blue-900">First sync in progress...</p>
                    <p className="text-xs text-blue-700">Fetching last 7 days of orders</p>
                  </div>
                </div>
              )}

              <button
                onClick={handleDone}
                className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-medium hover:from-blue-700 hover:to-blue-600 shadow-lg shadow-blue-500/30 transition-all"
              >
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
